/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { WorkDebtorOverviewCategoryEnum } from '../models/work-debtor-overview-category-enum';
import { WorkDebtorReminderFilterEnum } from '../models/work-debtor-reminder-filter-enum';
import { WorkDebtorsDueSummaryDto } from '../models/work-debtors-due-summary-dto';
import { WorkDebtorsOpenSummaryDto } from '../models/work-debtors-open-summary-dto';
import { WorkPaginatedDebtorOverviewDto } from '../models/work-paginated-debtor-overview-dto';
import { WorkPaymentReceiptDto } from '../models/work-payment-receipt-dto';

@Injectable({ providedIn: 'root' })
export class WorkDebtorsOverviewV2Service extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `debtorsOverviewV2ControllerGetList()` */
  static readonly DebtorsOverviewV2ControllerGetListPath = '/v2/debtors-overview/list';

  /**
   * Required permissions: debtors:read, debtors:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `debtorsOverviewV2ControllerGetList()` instead.
   *
   * This method doesn't expect any request body.
   */
  debtorsOverviewV2ControllerGetList$Response(
    params?: {
      category?: WorkDebtorOverviewCategoryEnum;

    /**
     * Projekt-Modus: nur Debitoren dieses Projekts, alle partial-Kategorien.
     */
      projectId?: number;

    /**
     * Kontakt-Modus: alle Debitoren über alle Projekte dieses Kontakts, alle partial-Kategorien.
     */
      contactId?: number;

    /**
     * Wenn true: nur unbezahlte Debitoren (paid IS NULL).
     */
      onlyOpen?: boolean;

    /**
     * Containertype-IDs zum Filtern (via Projekt → Container).
     */
      containertypeIds?: Array<number>;

    /**
     * Teilrechnungen (partial) zum Filtern, z.B. 1, 2.
     */
      partials?: Array<number>;

    /**
     * Zahlungserinnerung: aktuelle Stufe (1/2/3) bzw. Mahnstopp — mehrere werden mit ODER verknüpft.
     */
      reminders?: Array<WorkDebtorReminderFilterEnum>;

    /**
     * Suchbegriff: durchsucht Rechnungsnummer, QR-Nummer, Projektnummer, Kundenname (Vor-/Nachname, Firma) und die Rechnungsadresse am Debitor.
     */
      search?: string;

    /**
     * Anzahl Einträge pro Seite.
     */
      limit?: number;

    /**
     * Aktuelle Seite (1-basiert).
     */
      page?: number;

    /**
     * Sortierung im Format "spalte:ASC|DESC".
     */
      sortBy?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkPaginatedDebtorOverviewDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkDebtorsOverviewV2Service.DebtorsOverviewV2ControllerGetListPath, 'get');
    if (params) {
      rb.query('category', params.category, {});
      rb.query('projectId', params.projectId, {});
      rb.query('contactId', params.contactId, {});
      rb.query('onlyOpen', params.onlyOpen, {});
      rb.query('containertypeIds', params.containertypeIds, {});
      rb.query('partials', params.partials, {});
      rb.query('reminders', params.reminders, {});
      rb.query('search', params.search, {});
      rb.query('limit', params.limit, {});
      rb.query('page', params.page, {});
      rb.query('sortBy', params.sortBy, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkPaginatedDebtorOverviewDto>;
      })
    );
  }

  /**
   * Required permissions: debtors:read, debtors:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `debtorsOverviewV2ControllerGetList$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  debtorsOverviewV2ControllerGetList(
    params?: {
      category?: WorkDebtorOverviewCategoryEnum;

    /**
     * Projekt-Modus: nur Debitoren dieses Projekts, alle partial-Kategorien.
     */
      projectId?: number;

    /**
     * Kontakt-Modus: alle Debitoren über alle Projekte dieses Kontakts, alle partial-Kategorien.
     */
      contactId?: number;

    /**
     * Wenn true: nur unbezahlte Debitoren (paid IS NULL).
     */
      onlyOpen?: boolean;

    /**
     * Containertype-IDs zum Filtern (via Projekt → Container).
     */
      containertypeIds?: Array<number>;

    /**
     * Teilrechnungen (partial) zum Filtern, z.B. 1, 2.
     */
      partials?: Array<number>;

    /**
     * Zahlungserinnerung: aktuelle Stufe (1/2/3) bzw. Mahnstopp — mehrere werden mit ODER verknüpft.
     */
      reminders?: Array<WorkDebtorReminderFilterEnum>;

    /**
     * Suchbegriff: durchsucht Rechnungsnummer, QR-Nummer, Projektnummer, Kundenname (Vor-/Nachname, Firma) und die Rechnungsadresse am Debitor.
     */
      search?: string;

    /**
     * Anzahl Einträge pro Seite.
     */
      limit?: number;

    /**
     * Aktuelle Seite (1-basiert).
     */
      page?: number;

    /**
     * Sortierung im Format "spalte:ASC|DESC".
     */
      sortBy?: Array<string>;
    },
    context?: HttpContext
  ): Observable<WorkPaginatedDebtorOverviewDto> {
    return this.debtorsOverviewV2ControllerGetList$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkPaginatedDebtorOverviewDto>): WorkPaginatedDebtorOverviewDto => r.body)
    );
  }

  /** Path part for operation `debtorsOverviewV2ControllerGetPartials()` */
  static readonly DebtorsOverviewV2ControllerGetPartialsPath = '/v2/debtors-overview/partials';

  /**
   * Required permissions: debtors:read, debtors:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `debtorsOverviewV2ControllerGetPartials()` instead.
   *
   * This method doesn't expect any request body.
   */
  debtorsOverviewV2ControllerGetPartials$Response(
    params?: {
      category?: WorkDebtorOverviewCategoryEnum;

    /**
     * Projekt-Modus: nur Debitoren dieses Projekts, alle partial-Kategorien.
     */
      projectId?: number;

    /**
     * Kontakt-Modus: alle Debitoren über alle Projekte dieses Kontakts, alle partial-Kategorien.
     */
      contactId?: number;

    /**
     * Wenn true: nur unbezahlte Debitoren (paid IS NULL).
     */
      onlyOpen?: boolean;

    /**
     * Containertype-IDs zum Filtern (via Projekt → Container).
     */
      containertypeIds?: Array<number>;

    /**
     * Teilrechnungen (partial) zum Filtern, z.B. 1, 2.
     */
      partials?: Array<number>;

    /**
     * Zahlungserinnerung: aktuelle Stufe (1/2/3) bzw. Mahnstopp — mehrere werden mit ODER verknüpft.
     */
      reminders?: Array<WorkDebtorReminderFilterEnum>;

    /**
     * Suchbegriff: durchsucht Rechnungsnummer, QR-Nummer, Projektnummer, Kundenname (Vor-/Nachname, Firma) und die Rechnungsadresse am Debitor.
     */
      search?: string;

    /**
     * Anzahl Einträge pro Seite.
     */
      limit?: number;

    /**
     * Aktuelle Seite (1-basiert).
     */
      page?: number;

    /**
     * Sortierung im Format "spalte:ASC|DESC".
     */
      sortBy?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<number>>> {
    const rb = new RequestBuilder(this.rootUrl, WorkDebtorsOverviewV2Service.DebtorsOverviewV2ControllerGetPartialsPath, 'get');
    if (params) {
      rb.query('category', params.category, {});
      rb.query('projectId', params.projectId, {});
      rb.query('contactId', params.contactId, {});
      rb.query('onlyOpen', params.onlyOpen, {});
      rb.query('containertypeIds', params.containertypeIds, {});
      rb.query('partials', params.partials, {});
      rb.query('reminders', params.reminders, {});
      rb.query('search', params.search, {});
      rb.query('limit', params.limit, {});
      rb.query('page', params.page, {});
      rb.query('sortBy', params.sortBy, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<number>>;
      })
    );
  }

  /**
   * Required permissions: debtors:read, debtors:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `debtorsOverviewV2ControllerGetPartials$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  debtorsOverviewV2ControllerGetPartials(
    params?: {
      category?: WorkDebtorOverviewCategoryEnum;

    /**
     * Projekt-Modus: nur Debitoren dieses Projekts, alle partial-Kategorien.
     */
      projectId?: number;

    /**
     * Kontakt-Modus: alle Debitoren über alle Projekte dieses Kontakts, alle partial-Kategorien.
     */
      contactId?: number;

    /**
     * Wenn true: nur unbezahlte Debitoren (paid IS NULL).
     */
      onlyOpen?: boolean;

    /**
     * Containertype-IDs zum Filtern (via Projekt → Container).
     */
      containertypeIds?: Array<number>;

    /**
     * Teilrechnungen (partial) zum Filtern, z.B. 1, 2.
     */
      partials?: Array<number>;

    /**
     * Zahlungserinnerung: aktuelle Stufe (1/2/3) bzw. Mahnstopp — mehrere werden mit ODER verknüpft.
     */
      reminders?: Array<WorkDebtorReminderFilterEnum>;

    /**
     * Suchbegriff: durchsucht Rechnungsnummer, QR-Nummer, Projektnummer, Kundenname (Vor-/Nachname, Firma) und die Rechnungsadresse am Debitor.
     */
      search?: string;

    /**
     * Anzahl Einträge pro Seite.
     */
      limit?: number;

    /**
     * Aktuelle Seite (1-basiert).
     */
      page?: number;

    /**
     * Sortierung im Format "spalte:ASC|DESC".
     */
      sortBy?: Array<string>;
    },
    context?: HttpContext
  ): Observable<Array<number>> {
    return this.debtorsOverviewV2ControllerGetPartials$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<number>>): Array<number> => r.body)
    );
  }

  /** Path part for operation `debtorsOverviewV2ControllerGetDueSummary()` */
  static readonly DebtorsOverviewV2ControllerGetDueSummaryPath = '/v2/debtors-overview/due-summary';

  /**
   * Required permissions: debtors:read, debtors:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `debtorsOverviewV2ControllerGetDueSummary()` instead.
   *
   * This method doesn't expect any request body.
   */
  debtorsOverviewV2ControllerGetDueSummary$Response(
    params?: {
      category?: WorkDebtorOverviewCategoryEnum;

    /**
     * Projekt-Modus: nur Debitoren dieses Projekts, alle partial-Kategorien.
     */
      projectId?: number;

    /**
     * Kontakt-Modus: alle Debitoren über alle Projekte dieses Kontakts, alle partial-Kategorien.
     */
      contactId?: number;

    /**
     * Wenn true: nur unbezahlte Debitoren (paid IS NULL).
     */
      onlyOpen?: boolean;

    /**
     * Containertype-IDs zum Filtern (via Projekt → Container).
     */
      containertypeIds?: Array<number>;

    /**
     * Teilrechnungen (partial) zum Filtern, z.B. 1, 2.
     */
      partials?: Array<number>;

    /**
     * Zahlungserinnerung: aktuelle Stufe (1/2/3) bzw. Mahnstopp — mehrere werden mit ODER verknüpft.
     */
      reminders?: Array<WorkDebtorReminderFilterEnum>;

    /**
     * Suchbegriff: durchsucht Rechnungsnummer, QR-Nummer, Projektnummer, Kundenname (Vor-/Nachname, Firma) und die Rechnungsadresse am Debitor.
     */
      search?: string;

    /**
     * Anzahl Einträge pro Seite.
     */
      limit?: number;

    /**
     * Aktuelle Seite (1-basiert).
     */
      page?: number;

    /**
     * Sortierung im Format "spalte:ASC|DESC".
     */
      sortBy?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkDebtorsDueSummaryDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkDebtorsOverviewV2Service.DebtorsOverviewV2ControllerGetDueSummaryPath, 'get');
    if (params) {
      rb.query('category', params.category, {});
      rb.query('projectId', params.projectId, {});
      rb.query('contactId', params.contactId, {});
      rb.query('onlyOpen', params.onlyOpen, {});
      rb.query('containertypeIds', params.containertypeIds, {});
      rb.query('partials', params.partials, {});
      rb.query('reminders', params.reminders, {});
      rb.query('search', params.search, {});
      rb.query('limit', params.limit, {});
      rb.query('page', params.page, {});
      rb.query('sortBy', params.sortBy, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkDebtorsDueSummaryDto>;
      })
    );
  }

  /**
   * Required permissions: debtors:read, debtors:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `debtorsOverviewV2ControllerGetDueSummary$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  debtorsOverviewV2ControllerGetDueSummary(
    params?: {
      category?: WorkDebtorOverviewCategoryEnum;

    /**
     * Projekt-Modus: nur Debitoren dieses Projekts, alle partial-Kategorien.
     */
      projectId?: number;

    /**
     * Kontakt-Modus: alle Debitoren über alle Projekte dieses Kontakts, alle partial-Kategorien.
     */
      contactId?: number;

    /**
     * Wenn true: nur unbezahlte Debitoren (paid IS NULL).
     */
      onlyOpen?: boolean;

    /**
     * Containertype-IDs zum Filtern (via Projekt → Container).
     */
      containertypeIds?: Array<number>;

    /**
     * Teilrechnungen (partial) zum Filtern, z.B. 1, 2.
     */
      partials?: Array<number>;

    /**
     * Zahlungserinnerung: aktuelle Stufe (1/2/3) bzw. Mahnstopp — mehrere werden mit ODER verknüpft.
     */
      reminders?: Array<WorkDebtorReminderFilterEnum>;

    /**
     * Suchbegriff: durchsucht Rechnungsnummer, QR-Nummer, Projektnummer, Kundenname (Vor-/Nachname, Firma) und die Rechnungsadresse am Debitor.
     */
      search?: string;

    /**
     * Anzahl Einträge pro Seite.
     */
      limit?: number;

    /**
     * Aktuelle Seite (1-basiert).
     */
      page?: number;

    /**
     * Sortierung im Format "spalte:ASC|DESC".
     */
      sortBy?: Array<string>;
    },
    context?: HttpContext
  ): Observable<WorkDebtorsDueSummaryDto> {
    return this.debtorsOverviewV2ControllerGetDueSummary$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkDebtorsDueSummaryDto>): WorkDebtorsDueSummaryDto => r.body)
    );
  }

  /** Path part for operation `debtorsOverviewV2ControllerGetOpenSummary()` */
  static readonly DebtorsOverviewV2ControllerGetOpenSummaryPath = '/v2/debtors-overview/open-summary';

  /**
   * Required permissions: debtors:read, debtors:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `debtorsOverviewV2ControllerGetOpenSummary()` instead.
   *
   * This method doesn't expect any request body.
   */
  debtorsOverviewV2ControllerGetOpenSummary$Response(
    params?: {

    /**
     * Containertype-IDs zum Filtern (via Projekt → Container).
     */
      containertypeIds?: Array<number>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkDebtorsOpenSummaryDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkDebtorsOverviewV2Service.DebtorsOverviewV2ControllerGetOpenSummaryPath, 'get');
    if (params) {
      rb.query('containertypeIds', params.containertypeIds, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkDebtorsOpenSummaryDto>;
      })
    );
  }

  /**
   * Required permissions: debtors:read, debtors:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `debtorsOverviewV2ControllerGetOpenSummary$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  debtorsOverviewV2ControllerGetOpenSummary(
    params?: {

    /**
     * Containertype-IDs zum Filtern (via Projekt → Container).
     */
      containertypeIds?: Array<number>;
    },
    context?: HttpContext
  ): Observable<WorkDebtorsOpenSummaryDto> {
    return this.debtorsOverviewV2ControllerGetOpenSummary$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkDebtorsOpenSummaryDto>): WorkDebtorsOpenSummaryDto => r.body)
    );
  }

  /** Path part for operation `debtorsOverviewV2ControllerGetPaymentReceipts()` */
  static readonly DebtorsOverviewV2ControllerGetPaymentReceiptsPath = '/v2/debtors-overview/payment-receipts';

  /**
   * Required permissions: debtors:read, debtors:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `debtorsOverviewV2ControllerGetPaymentReceipts()` instead.
   *
   * This method doesn't expect any request body.
   */
  debtorsOverviewV2ControllerGetPaymentReceipts$Response(
    params: {

    /**
     * Von-Datum (inklusive), Format YYYY-MM-DD.
     */
      fromDate: string;

    /**
     * Bis-Datum (inklusive), Format YYYY-MM-DD.
     */
      toDate: string;

    /**
     * Containertype-IDs zum Filtern (via Projekt → Container).
     */
      containertypeIds?: Array<number>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<WorkPaymentReceiptDto>>> {
    const rb = new RequestBuilder(this.rootUrl, WorkDebtorsOverviewV2Service.DebtorsOverviewV2ControllerGetPaymentReceiptsPath, 'get');
    if (params) {
      rb.query('fromDate', params.fromDate, {});
      rb.query('toDate', params.toDate, {});
      rb.query('containertypeIds', params.containertypeIds, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<WorkPaymentReceiptDto>>;
      })
    );
  }

  /**
   * Required permissions: debtors:read, debtors:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `debtorsOverviewV2ControllerGetPaymentReceipts$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  debtorsOverviewV2ControllerGetPaymentReceipts(
    params: {

    /**
     * Von-Datum (inklusive), Format YYYY-MM-DD.
     */
      fromDate: string;

    /**
     * Bis-Datum (inklusive), Format YYYY-MM-DD.
     */
      toDate: string;

    /**
     * Containertype-IDs zum Filtern (via Projekt → Container).
     */
      containertypeIds?: Array<number>;
    },
    context?: HttpContext
  ): Observable<Array<WorkPaymentReceiptDto>> {
    return this.debtorsOverviewV2ControllerGetPaymentReceipts$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<WorkPaymentReceiptDto>>): Array<WorkPaymentReceiptDto> => r.body)
    );
  }

  /** Path part for operation `debtorsOverviewV2ControllerGetZessionslisteExcel()` */
  static readonly DebtorsOverviewV2ControllerGetZessionslisteExcelPath = '/v2/debtors-overview/zessionsliste-excel';

  /**
   * Required permissions: debtors:read, debtors:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `debtorsOverviewV2ControllerGetZessionslisteExcel()` instead.
   *
   * This method doesn't expect any request body.
   */
  debtorsOverviewV2ControllerGetZessionslisteExcel$Response(
    params?: {

    /**
     * Containertype-IDs zum Filtern (via Projekt → Container).
     */
      containertypeIds?: Array<number>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(this.rootUrl, WorkDebtorsOverviewV2Service.DebtorsOverviewV2ControllerGetZessionslisteExcelPath, 'get');
    if (params) {
      rb.query('containertypeIds', params.containertypeIds, {});
    }

    return this.http.request(
      rb.build({ responseType: 'text', accept: '*/*', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Required permissions: debtors:read, debtors:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `debtorsOverviewV2ControllerGetZessionslisteExcel$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  debtorsOverviewV2ControllerGetZessionslisteExcel(
    params?: {

    /**
     * Containertype-IDs zum Filtern (via Projekt → Container).
     */
      containertypeIds?: Array<number>;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.debtorsOverviewV2ControllerGetZessionslisteExcel$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

}
